import { composeRequestPipeline, composeResponsePipeline } from "./pipeline";
import { ApiOptions, ApiResult, err, ok, RequestMiddleware, ResponseMiddleware } from "./types";

const buildUrl = (baseUrl: string, url: string, params?: Record<string, unknown>) => {
    const full = new URL(url, baseUrl);

    if (params) {
        for (const [k, v] of Object.entries(params)) {
            if (v !== undefined && v !== null) {
                full.searchParams.append(k, String(v));
            }
        }
    }

    return full.toString();
}

type HttpClientConfig = {
    baseUrl: string;
    requestMiddlewares?: RequestMiddleware[];
    responseMiddlewares?: ResponseMiddleware[];
};

const buildRequest = (fullUrl: string, method: string, body?: unknown, options?: ApiOptions): Request =>
    new Request(fullUrl, {
        method,
        headers: { 'Content-Type': 'application/json', ...options?.headers },
        signal: options?.signal,
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });


const toApiError = async (response: Response): Promise<ApiResult<never>> => {
    const data = await response.json().catch(() => null);
    return err({
        status: response.status,
        message: data?.title ?? response.statusText ?? 'Request failed',
        errors: data?.errors ?? {},
    });
};

const toCatchError = (e: unknown): ApiResult<never> => {
    if (e instanceof DOMException && e.name === 'AbortError')
        return err({ status: 0, message: 'ERR_CANCELED', errors: {} });
    if (e instanceof TypeError)
        return err({ status: 0, message: 'ERR_NETWORK', errors: {} });
    return err({ status: 500, message: (e as Error).message, errors: {} });
};

export const createHttpClient = (config: HttpClientConfig) => {
    const requestPipeline = composeRequestPipeline(config.requestMiddlewares ?? []);
    const responsePipeline = composeResponsePipeline(config.responseMiddlewares ?? []);

    const request = async <T>(url: string, method: string, body?: unknown, options?: ApiOptions): Promise<ApiResult<T>> => {
        try {
            const initRequest = buildRequest(buildUrl(config.baseUrl, url, options?.params), method, body, options);
            const finalRequest = await requestPipeline(initRequest);
            const rawResponse = await fetch(finalRequest)
            const { response } = await responsePipeline({
                request: finalRequest,
                response: rawResponse
            });

            if (!response.ok) return toApiError(response);

            const data = await response.json().catch(() => null);
            return ok<T>(data);
        } catch (e) {
            return toCatchError(e);
        }
    };

    return {
        baseUrl: config.baseUrl,

        get: <T>(url: string, options?: ApiOptions) =>
            request<T>(url, 'GET', undefined, options),

        post: <T>(url: string, data?: unknown, options?: ApiOptions) =>
            request<T>(url, 'POST', data, options),

        put: <T>(url: string, data?: unknown, options?: ApiOptions) =>
            request<T>(url, 'PUT', data, options),

        patch: <T>(url: string, data?: unknown, options?: ApiOptions) =>
            request<T>(url, 'PATCH', data, options),

        delete: <T = void>(url: string, options?: ApiOptions) =>
            request<T>(url, 'DELETE', undefined, options),
    };
}